import type { ReactNode } from "react";
import rawContent from "./marketplace-content.yaml";
import { MARKETPLACE_ICONS } from "./marketplace-icons";

/* ────── Types ────── */

export interface FeaturedGuide {
  name: string;
  slug: string;
  desc: string;
  icon: ReactNode;
  iconBg: string;
  buttonBg: string;
  rating: string;
  ratingNum: number;
  installs: string;
}

export interface CategoryGuide {
  name: string;
  author: string;
  desc: string;
  rating: string;
  installs: string;
}

export interface Category {
  name: string;
  slug: string;
  count: number;
  icon: ReactNode;
  color: string;
  desc: string;
  guides: CategoryGuide[];
}

export interface Capability {
  title: string;
  desc: string;
  icon: ReactNode;
  color: string;
}

export interface Preview {
  title: string;
  subtitle?: string;
  value?: string;
  unit?: string;
  type: "metric" | "checklist" | "chart";
}

export interface Creator {
  name: string;
  desc: string;
  verified: boolean;
}

export interface Review {
  quote: string;
  detail: string;
}

export interface GuideDetail {
  name: string;
  slug: string;
  installs: string;
  rating: string;
  ratingNum: number;
  buttonBg: string;
  subtitle: string;
  about: string[];
  capabilities: Capability[];
  previews: Preview[];
  creator: Creator;
  reviews: Review[];
}

/* ────── Helpers ────── */

function resolveIcon(key: string): ReactNode {
  const icon = MARKETPLACE_ICONS[key];
  if (!icon) {
    console.warn(`[marketplace] Unknown icon key: "${key}"`);
    return null;
  }
  return icon;
}

/* ────── Raw YAML shape ────── */

interface RawCapability {
  title: string;
  desc: string;
  icon: string;
  color: string;
}

interface RawPreview {
  title: string;
  subtitle?: string;
  value?: string;
  unit?: string;
  type: "metric" | "checklist" | "chart";
}

interface RawDetail {
  subtitle: string;
  about: string[];
  capabilities: RawCapability[];
  previews: RawPreview[];
  creator: Creator;
  reviews: Review[];
}

interface RawFeatured {
  name: string;
  slug: string;
  desc: string;
  icon: string;
  iconBg: string;
  buttonBg: string;
  rating: string;
  ratingNum: number;
  installs: string;
  detail: RawDetail;
}

interface RawCategoryGuide {
  name: string;
  author: string;
  desc: string;
  rating: string;
  installs: string;
}

interface RawCategory {
  name: string;
  slug: string;
  count: number;
  icon: string;
  color: string;
  desc: string;
  guides: RawCategoryGuide[];
}

interface RawContent {
  featured: RawFeatured[];
  categories: RawCategory[];
}

/* ────── Resolved data ────── */

const content = rawContent as unknown as RawContent;

export const FEATURED: FeaturedGuide[] = content.featured.map((p) => ({
  name: p.name,
  slug: p.slug,
  desc: p.desc,
  icon: resolveIcon(p.icon),
  iconBg: p.iconBg,
  buttonBg: p.buttonBg,
  rating: p.rating,
  ratingNum: p.ratingNum,
  installs: p.installs,
}));

export const CATEGORIES: Category[] = content.categories.map((c) => ({
  name: c.name,
  slug: c.slug,
  count: c.count,
  icon: resolveIcon(c.icon),
  color: c.color,
  desc: c.desc,
  guides: c.guides,
}));

export function getGuideBySlug(slug: string): GuideDetail | undefined {
  const raw = content.featured.find((p) => p.slug === slug);
  if (!raw) return undefined;

  const d = raw.detail;
  return {
    name: raw.name,
    slug: raw.slug,
    installs: raw.installs,
    rating: raw.rating,
    ratingNum: raw.ratingNum,
    buttonBg: raw.buttonBg,
    subtitle: d.subtitle,
    about: d.about,
    capabilities: d.capabilities.map((c) => ({
      title: c.title,
      desc: c.desc,
      icon: resolveIcon(c.icon),
      color: c.color,
    })),
    previews: d.previews,
    creator: d.creator,
    reviews: d.reviews,
  };
}

export function getCategoryBySlug(slug: string): Category | undefined {
  return CATEGORIES.find((c) => c.slug === slug);
}
